import type { ReploIntegration } from "schemas/generated/integration";
import type { IntegrationType } from "./IntegrationForm";

import * as React from "react";

import IntegrationCard from "@editor/components/dashboard/integrations/IntegrationCard";
import { LoadedSettings } from "@editor/components/shops-ui/settings/SettingsCard";
import { ShopifyIntegration } from "@editor/components/shops-ui/settings/shop/integrations/ShopifyIntegration";
import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useGetIntegrationsQuery } from "@editor/reducers/sanity-reducer";
import { integrationRouter } from "@editor/trpc/integration";
import { stripeConnectRouter } from "@editor/trpc/stripeConnect";
import { routes } from "@editor/utils/router";

import IconButton from "@replo/design-system/components/button/IconButton";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import EmptyState from "@replo/design-system/components/empty-state/EmptyState";
import { LargeMenuItem } from "@replo/design-system/components/menu/LargeMenuItem";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { skipToken } from "@tanstack/react-query";
import { ChevronDown, Pencil, Puzzle, Search } from "lucide-react";
import { generatePath, useNavigate } from "react-router-dom";

import { INTEGRATION_CONFIGS } from "./config";
import { IntegrationForm } from "./IntegrationForm";

const INTEGRATIONS_COMBOBOX_OPTIONS = Object.entries(INTEGRATION_CONFIGS).map(
  ([integrationType, { title, description, image }]) => ({
    value: integrationType,
    label: (
      <LargeMenuItem
        variant="flexible"
        label={title}
        description={description}
        startEnhancer={
          <img
            src={image}
            alt={title}
            style={{ width: "32px", height: "32px" }}
            className="rounded-lg"
          />
        }
      />
    ),
    displayValue: title,
    estimatedSize: 50,
  }),
);

const IntegrationsCombobox: React.FC<{
  integration: IntegrationType | null;
  onIntegrationChange: (value: IntegrationType) => void;
}> = ({ integration, onIntegrationChange }) => {
  return (
    <Combobox.Root
      options={INTEGRATIONS_COMBOBOX_OPTIONS}
      value={integration ?? ""}
      onChange={(value) => {
        onIntegrationChange(value as IntegrationType);
      }}
    >
      <Combobox.Trigger>
        {/* Note (Noah, 2025-09-29, REPL-19758): This wrapper div needs to be here
        so that the combobox popover has full width. Apparently radix doesn't correctly
        recognize the trigger in this case */}
        <div className="flex flex-col w-full">
          <Combobox.Input
            placeholder="Search"
            startEnhancer={<Search className="h-3 w-3" />}
            outlineOnActive
            size="base"
            endEnhancer={<ChevronDown className="h-3 w-3" />}
          />
        </div>
      </Combobox.Trigger>
      <Combobox.Popover>
        <Combobox.Content />
      </Combobox.Popover>
    </Combobox.Root>
  );
};

const AddIntegrationSection: React.FC = () => {
  const [integration, setIntegration] = React.useState<IntegrationType | null>(
    null,
  );

  // NOTE (Max, 2025-07-31): If an integration is selected, we show the form that allows the user
  // to enter the integration config details. Otherwise, we show the combobox that allows them
  // to select an integration.
  return (
    <>
      {integration ? (
        <IntegrationForm
          integration={integration}
          handleCancel={() => setIntegration(null)}
        />
      ) : (
        <IntegrationsCombobox
          integration={integration}
          onIntegrationChange={setIntegration}
        />
      )}
    </>
  );
};

const IntegrationsHome: React.FC = () => {
  const { data: otherIntegrations } = useGetIntegrationsQuery({});
  const { project } = useCurrentProjectContext();
  const isReploHosted = project?.hostingType === "replo";

  const sections = [
    {
      title: "Available Integrations",
      description:
        "Helpful guides to get you up and running with your favorite integrations.",
      component: (
        <div className="flex flex-col gap-4 w-full overflow-hidden">
          <ShopifyIntegrationComponent />

          <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 min-w-0 w-full">
            {otherIntegrations &&
              otherIntegrations.map((integration) => (
                <IntegrationCard
                  key={integration.name}
                  title={integration.name}
                  image={integration.image}
                  redirectUrl={integration.documentationUrl}
                />
              ))}
          </div>
        </div>
      ),
    },
  ];

  const sectionsV2 = [
    {
      title: "Add an Integration",
      description: "Find your integration and plug in the details to connect.",
      component: <AddIntegrationSection />,
    },
    {
      title: "Your Integrations",
      component: <IntegrationsListSection />,
    },
  ];

  return <LoadedSettings sections={isReploHosted ? sectionsV2 : sections} />;
};

const ShopifyIntegrationComponent: React.FC = () => {
  const { project } = useCurrentProjectContext();
  const projectId = useCurrentProjectId();

  // Get Stripe Connect onboarding status
  const { data: onboardingStatus } =
    stripeConnectRouter.stripeConnectGetOnboardingStatus.useQuery(
      projectId ? { projectId } : skipToken,
    );

  if (!project) {
    return <Skeleton className="h-[190px] w-full" />;
  }

  // Only show Shopify integration if Stripe Connect is not fully onboarded
  const isStripeFullyOnboarded =
    onboardingStatus?.isConnected && onboardingStatus?.onboardingCompleted;

  if (isStripeFullyOnboarded) {
    return null;
  }

  return (
    <div className="border-0.5 border-border rounded-xl p-4">
      <ShopifyIntegration project={project} />
    </div>
  );
};

const IntegrationsListSection: React.FC = () => {
  const projectId = useCurrentProjectId();

  const { data: integrations, isLoading: isLoadingIntegrations } =
    integrationRouter.find.useQuery(
      projectId
        ? {
            projectId,
          }
        : skipToken,
    );

  return (
    <div className="space-y-4 w-full overflow-hidden">
      <ShopifyIntegrationComponent />
      {isLoadingIntegrations ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 min-w-0 w-full">
          <Skeleton className="h-[143px] w-full" />
          <Skeleton className="h-[143px] w-full" />
          <Skeleton className="h-[143px] w-full" />
          <Skeleton className="h-[143px] w-full" />
        </div>
      ) : (
        <LoadedIntegrations
          integrations={integrations ?? []}
          projectId={projectId ?? ""}
        />
      )}
    </div>
  );
};

const LoadedIntegrations: React.FC<{
  integrations: ReploIntegration[];
  projectId: string;
}> = ({ integrations, projectId }) => {
  const navigate = useNavigate();

  const nonShopifyIntegrations = integrations.filter(
    // NOTE (Max, 2025-07-31): We show the Shopify Integration separately,
    // so we don't need to show it here again.
    // NOTE: We also show StripeConnect separately, so exclude it too.
    (integration) =>
      integration.type !== "ShopifyIntegrationConfig" &&
      integration.type !== "StripeConnect",
  );

  if (nonShopifyIntegrations.length == 0) {
    return (
      <div className="flex justify-center items-center">
        <EmptyState
          icon={<Puzzle />}
          title="No other integrations found"
          description="Search and add another integration above."
          variant="noRings"
          size="sm"
        />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 min-w-0 w-full">
      {nonShopifyIntegrations?.map((integration) => {
        const { title, description, image } =
          INTEGRATION_CONFIGS[integration.type];

        return (
          <div
            key={integration.id}
            className="p-4 border-0.5 border-border rounded-lg hover:bg-button-secondary-hover cursor-pointer min-h-[120px] w-full min-w-0 max-w-full"
            onClick={() => {
              navigate(
                generatePath(routes.shops.settings.shops.integrations.edit, {
                  projectId,
                  integrationId: integration.id,
                }),
              );
            }}
          >
            <div className="flex flex-col gap-2">
              <div className="flex flex-row justify-between">
                <img
                  src={image}
                  alt={title}
                  style={{
                    width: "32px",
                    height: "32px",
                  }}
                  className="rounded-lg"
                />
                <IconButton
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={undefined}
                  icon={<Pencil className="h-3 w-3" />}
                />
              </div>

              <div className="flex flex-col gap-0.5">
                <span className="header-h4 break-words">{title}</span>
                <span className="text-sm text-muted break-words leading-relaxed">
                  {description}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default IntegrationsHome;
